import React, { useState } from "react";
import { motion } from "framer-motion";
import { Search, Rocket, Plane, Cog } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/AppContext";
import api from "@/api";
import Navbar from "@/components/Navbar";
import RadarScan from "@/components/RadarScan";
import ResultsTable from "@/components/ResultsTable";
import MembershipModal from "@/components/MembershipModal";
import CartDrawer from "@/components/CartDrawer";
import SellerModal from "@/components/SellerModal";
import OrdersModal from "@/components/OrdersModal";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function Dashboard() {
  const { t, refreshUser } = useApp();
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const [showMembership, setShowMembership] = useState(false);
  const [showCart, setShowCart] = useState(false);
  const [showSeller, setShowSeller] = useState(false);
  const [showOrders, setShowOrders] = useState(false);

  const cats = [
    { id: "drone", label: t("drone"), icon: Rocket },
    { id: "aircraft", label: t("aircraft"), icon: Plane },
    { id: "machinery", label: t("machinery"), icon: Cog },
  ];

  const doSearch = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    if (!category) {
      toast.error(t("select_category_first"));
      return;
    }
    setLoading(true);
    setData(null);
    try {
      const { data: res } = await api.post("/search", { query, category });
      setData(res);
      refreshUser();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 402) {
        toast.error(t("limit_reached"));
        setShowMembership(true);
      } else {
        toast.error(typeof detail === "string" ? detail : "Search error");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen ps-grid-bg">
      <Navbar
        onCart={() => setShowCart(true)}
        onMembership={() => setShowMembership(true)}
        onOrders={() => setShowOrders(true)}
        onSell={() => setShowSeller(true)}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 tracking-tight">
            <span className="ps-neon-text">AI</span> SPARE PART SCANNER
          </h1>
          <p className="text-slate-400 mt-3 max-w-2xl mx-auto">{t("hero_sub")}</p>
        </motion.div>

        {/* Search box */}
        <form onSubmit={doSearch} className="max-w-3xl mx-auto ps-glow-card rounded-2xl p-4 sm:p-6 border-cyan-500/40" data-testid="search-box">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-cyan-600" size={18} />
              <Input
                data-testid="search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("search_placeholder")}
                className="pl-10 bg-slate-50 border-slate-300 h-12 text-slate-900"
              />
            </div>
            <Button
              data-testid="ai-search-btn"
              type="submit"
              disabled={loading}
              className="h-12 px-8 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-head font-bold"
            >
              <Search size={18} className="mr-2" /> {t("search_btn")}
            </Button>
          </div>

          <div className="flex flex-wrap gap-2 mt-4">
            {cats.map((c) => (
              <button
                key={c.id}
                type="button"
                data-testid={`category-${c.id}`}
                onClick={() => setCategory(c.id)}
                className={`flex items-center gap-1.5 text-sm font-head font-semibold rounded-full px-4 py-1.5 border transition ${
                  category === c.id
                    ? "bg-cyan-500/20 border-cyan-400 text-cyan-700"
                    : "border-slate-300 text-slate-500 hover:border-cyan-500/50"
                }`}
              >
                <c.icon size={15} /> {c.label}
              </button>
            ))}
          </div>
        </form>

        {/* Results / Radar */}
        <div className="mt-10 max-w-4xl mx-auto">
          {loading && <RadarScan />}
          {!loading && data && <ResultsTable data={data} />}
        </div>

        <footer className="mt-16 pt-8 border-t border-slate-200 text-center text-sm text-slate-500 font-mono-ps">
          {t("contact_us")} · PartSpare.net © 2026
        </footer>
      </main>

      <MembershipModal open={showMembership} onOpenChange={setShowMembership} />
      <CartDrawer open={showCart} onOpenChange={setShowCart} />
      <SellerModal open={showSeller} onOpenChange={setShowSeller} />
      <OrdersModal open={showOrders} onOpenChange={setShowOrders} />
    </div>
  );
}
